import { Component, ElementRef, EventEmitter, Input, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {DfMeDropdownChanges} from './tree-node/df-me-dropdown-node/df-me-dropdown-node.component';
import {DfMeDropdownNode} from './models/DfMeDropdownNode';
import {DfMeDropdownService} from './df-me-dropdown.service';
import {Subscription} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { safeJsonInput } from '../../_utils/safe-json-input';
@Component({
    selector: 'gtw-df-me-dropdown',
    templateUrl: './df-me-dropdown.component.html',
    styleUrls: ['./df-me-dropdown.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DfMeDropdownComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);


  data: DfMeDropdownNode[];
  // Initialized to `false` (not `undefined`) so the very first template
  // pass renders a stable, defined value for `[ngClass]="{'disabled': loading}"`.
  // See `loadMEsCustom` below for the related NG0100 fix.
  loading = false;
  selected: Set<string>;
  selectedParentMe!: DfMeDropdownNode;
  showDropContainer: boolean;
  showRightMenu: boolean;
  dropdownTop = 0;
  dropdownLeft = 0;

  private _preSelectedKeys = '';

  @Input() customActionCode: number | null;
  @Input() buttonLabel: string = 'ME(s)';

  @Input()
  set preSelectedKeys(keys: string) {
    this._preSelectedKeys = keys || '';
    this.applyPreSelection();
  }

  @Input()
  set meData(nodes: any[]) {
    // Snapshot the user's selection before we rebuild the tree. The previous
    // implementation called `this.selected.clear()` here unconditionally, which
    // wiped the user's picks every time the parent re-emitted `[meData]` —
    // happens after cascades, after `refresh()` re-runs `setTabActive`, after
    // `globalParamsChanged$`, etc. We instead preserve picks for keys that
    // still exist in the new tree and silently drop the rest.
    const previouslySelected = new Set<string>(this.selected);
    // New component instance (e.g. workspace tab switch) starts with an empty
    // `this.selected` but the parent still has `filter.selected` — those keys
    // arrive via `[preSelectedKeys]`. Merge them before restoreSelection so we
    // do not briefly treat the selection as empty and show "all" / wrong counts.
    this.mergeInputPreSelectedIntoSet(previouslySelected);

    if (nodes && nodes.length > 0) {
      this.data = this.normalizeNodes(nodes);
      this.showRightMenu = false;
      this.selected = new Set<string>();
      this.data.forEach((node: DfMeDropdownNode) => {
        this.countChildren(node);
        this.restoreSelection(node, previouslySelected);
      });
      this.applyPreSelection();
    } else {
      // Empty / stale tree (cascade in progress, HTTP error, race between
      // `filter.values = []` reset and the new payload landing). DO NOT
      // wipe `this.selected` here — keep the user's prior picks intact so
      // the next `meData` emission with a real tree can restore them via
      // `restoreSelection`. Wiping here used to cascade into `emitChanges`
      // → `filter.selected = []` upstream → empty `parent_me_string` →
      // `loadGridData` fallback to `global_selected_me` → grid loaded for
      // ALL MEs (the very regression this fix addresses).
      this.data = [];
      this.showRightMenu = false;
    }
    this.loading = false;

    // If the new tree no longer contains some keys the user had selected,
    // notify the parent so its `filter.selected` stays in sync with what we
    // visually show. We deliberately skip the emit when:
    //   - nothing was dropped (selection survived the refresh untouched), OR
    //   - the incoming tree was empty/null (transient state — see comment
    //     above; emitting here would fire `selectedMes=''` upstream and
    //     wipe `filter.selected`).
    if (nodes && nodes.length > 0
        && previouslySelected.size > 0
        && this.selected.size !== previouslySelected.size) {
      this.emitChanges();
    }
  }

  /**
   * Re-applies the user's previous selection to a freshly normalized node tree
   * by matching node `key`. Updates `node.selected`, `node.numSelected`, and
   * keeps `this.selected` (the flat key set) in sync.
   *
   * Returns the count of children selected under (and including) this node so
   * parents can roll it up.
   */
  private restoreSelection(node: DfMeDropdownNode, previous: Set<string>): number {
    let descendantsSelected = 0;
    if (node.children && node.children.length) {
      node.children.forEach((child: DfMeDropdownNode) => {
        descendantsSelected += this.restoreSelection(child, previous);
      });
    }

    if (previous.has(node.key)) {
      node.selected = true;
      this.selected.add(node.key);
      // Children of a previously fully-selected parent: mark all descendants
      // selected too (a parent ME being selected meant the whole subtree was).
      if (node.children && node.children.length && descendantsSelected === 0) {
        const counter = { value: 0 };
        this.setChildren(node, counter, true);
        descendantsSelected = counter.value;
      }
    }

    if (node.children && node.children.length) {
      node.numSelected = descendantsSelected;
    }

    return (node.selected ? 1 : 0) + descendantsSelected;
  }

  @Output() selectedMes: EventEmitter<string>;

  constructor(
    private meService: DfMeDropdownService,
    private elRef: ElementRef,
  ) {
    this.data = [];
    this.selected = new Set<string>();
    this.showDropContainer = false;
    this.showRightMenu = false;
    this.customActionCode = null;
    this.selectedMes = new EventEmitter<string>();
  }

  ngOnInit(): void {
  }

  /** Seeds selection restoration from `preSelectedKeys` when internal Set is empty (tab return). */
  private mergeInputPreSelectedIntoSet(target: Set<string>): void {
    if (!this._preSelectedKeys?.trim()) return;
    for (const part of this._preSelectedKeys.split(',')) {
      const k = part.trim();
      if (k) target.add(k);
    }
  }

  private normalizeNodes(nodes: any[]): DfMeDropdownNode[] {
    return nodes.map((node: any) => {
      const n: any = {...node};
      if (!n.name && n.NAME) n.name = n.NAME;
      if (!n.name && n.LABEL) n.name = n.LABEL;
      if (!n.name && n.label) n.name = n.label;
      if (!n.key && n.KEY) n.key = n.KEY;
      if (!n.key && n.VALUE) n.key = n.VALUE;
      if (!n.key && n.ME_KEY) n.key = n.ME_KEY;
      n.selected = n.selected || false;
      n.numSelected = n.numSelected || 0;
      if (n.children && n.children.length) {
        n.children = this.normalizeNodes(n.children);
      }
      return n as DfMeDropdownNode;
    });
  }

  loadMEsCustom(taxYear: string): void {
    // Defer all synchronous mutations of template-bound state (`loading`,
    // `data`, `selected`, `showDropContainer`, `showRightMenu`) to a
    // microtask. Several callers invoke this from inside their own change
    // detection cycle (e.g. StatementTrackerComponent.ngAfterViewInit and
    // .applyWorkspaceContext, every DST report's onTaxYearChange, etc. —
    // see DST_MODULE_KNOWLEDGE.md section "DfMeDropdown NG0100 fix"),
    // which would otherwise trigger NG0100
    // ExpressionChangedAfterItHasBeenCheckedError on the
    // `[ngClass]="{'disabled': loading}"` binding (loading: undefined→true)
    // in dev mode. Centralising the defer here covers all current and
    // future call sites without requiring each caller to wrap in setTimeout.
    Promise.resolve().then(() => this._loadMEsCustomImpl(taxYear));
  }

  private _loadMEsCustomImpl(taxYear: string): void {
    this.data = [];
    this.selected.clear();
    this.showDropContainer = false;
    this.showRightMenu = false;
    this.loading = true;
    try {
      this.meService.loadMEs(taxYear, this.customActionCode).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((data: CustomMENodeResponse[] | null) => {
          const nodes = Array.isArray(data) ? data : [];
          nodes.forEach((c: CustomMENodeResponse) => {
            const meNode: DfMeDropdownNode = safeJsonInput<any>(c.JSON_TEST, null, 'df-me-dropdown.parse1');
            if (!meNode) {
              return;
            }
            this.countChildren(meNode);
            this.data.push(meNode);
          });
        }, (error: HttpErrorResponse) => {

        }, () => {
          this.loading = false;
        });
      // TODO add loading status
    } catch (e: any) {
      if (e instanceof SyntaxError) {
        console.log('JSON parse Syntax Error' + e);
        // TODO show alert message
      }
      this.loading = false;
    }
  }

  private applyPreSelection(): void {
    if (!this._preSelectedKeys || !this.data?.length) return;
    const keySet = new Set(
      this._preSelectedKeys.split(',').map(k => k.trim()).filter(Boolean)
    );
    if (keySet.size === 0) return;
    if (keySet.size === this.selected.size && [...keySet].every(k => this.selected.has(k))) return;

    this.selected.clear();
    this.walkAndSelect(this.data, keySet);
  }

  private walkAndSelect(nodes: DfMeDropdownNode[], keySet: Set<string>): void {
    for (const node of nodes) {
      const match = keySet.has(String(node.key));
      node.selected = match;
      if (match) {
        this.selected.add(node.key);
      }
      if (node.children?.length) {
        this.walkAndSelect(node.children, keySet);
        node.numSelected = this.countSelectedDescendants(node);
        if (node.numSelected === node.totalChildrenCount && node.totalChildrenCount! > 0) {
          node.selected = true;
          this.selected.add(node.key);
        }
      }
    }
  }

  private countSelectedDescendants(node: DfMeDropdownNode): number {
    let count = 0;
    if (!node.children) return count;
    for (const child of node.children) {
      if (child.selected) count++;
      if (child.children?.length) count += this.countSelectedDescendants(child);
    }
    return count;
  }

  private countChildren(node: DfMeDropdownNode): number {
    if (!node.children) {
      return 1;
    }
    node.numSelected = 0;
    node.totalChildrenCount = 0;
    node.children.forEach((child: DfMeDropdownNode) => {
      node.totalChildrenCount! += this.countChildren(child);
    });
    return node.totalChildrenCount + 1;
  }

  toggleMenu(): void {
    this.showDropContainer = !this.showDropContainer;
    if (this.showDropContainer) {
      this.updateDropdownPosition();
    }
  }

  private updateDropdownPosition(): void {
    const btn: HTMLElement = this.elRef.nativeElement.querySelector('.drop-down-btn');
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    // Reserve enough horizontal space for the left menu plus the right pane
    // (children tree) so badges/tree never get clipped at the viewport edge.
    const estimatedWidth = this.showRightMenu ? 820 : 230;
    const margin = 8;

    let left = rect.left;
    if (left + estimatedWidth > viewportWidth - margin) {
      left = Math.max(margin, viewportWidth - estimatedWidth - margin);
    }

    this.dropdownTop = rect.bottom;
    this.dropdownLeft = left;
  }

  selectAll(): void {
    this.data.forEach((parentMe: DfMeDropdownNode) => {
      parentMe.selected = true;
      this.selectParentMe(parentMe);
    });
    this.emitChanges();
  }

  unselectAll(): void {
    this.clear();
    this.emitChanges();
  }

  clear(): void {
    this.data.forEach((parentMe: DfMeDropdownNode) => {
      parentMe.selected = false;
      this.selectParentMe(parentMe, false);
    });
  }

  onParentMeSelected(parentMe: DfMeDropdownNode): void {
    this.selectParentMe(parentMe, parentMe.selected);
    if (parentMe.children && parentMe.children.length) {
      this.expandParentME(parentMe);
    }
    this.emitChanges();
  }

  private selectParentMe(parentMe: DfMeDropdownNode, select: boolean = true): void {
    let counter = {value: 0};
    if (select) {
      this.selected.add(parentMe.key);
    } else {
      this.removeFromSelected(parentMe.key);
    }
    this.setChildren(parentMe, counter, select);
    parentMe.numSelected = !select ? 0 : counter.value;
  }

  // set children selection recursively
  private setChildren(node: DfMeDropdownNode, counter: any, select: boolean = true): void {
    if (!node.children) {
      return;
    }
    node.children.forEach((child: DfMeDropdownNode) => {
      child.selected = select;
      if (select) {
        this.selected.add(child.key);
        node.numSelected!++;
        counter.value++;
      } else {
        this.removeFromSelected(child.key);
        node.numSelected!--;
      }
      this.setChildren(child, counter, select);
    });
  }

  private removeFromSelected(key: string): void {
    this.selected.delete(key);
  }

  expandParentME(item: DfMeDropdownNode): void {
    const wasRightMenuOpen = this.showRightMenu;
    this.selectedParentMe = item;
    this.showRightMenu = true;
    // First time the right pane opens, the dropdown roughly doubles in width;
    // re-clamp to viewport so the children tree doesn't get clipped on the right.
    if (!wasRightMenuOpen) {
      this.updateDropdownPosition();
    }
  }

  onChildrenSelectionChanged(changes: DfMeDropdownChanges): void {
    if (changes.select) {
      this.selectedParentMe.numSelected! += changes.changedNodes.length;
      if (this.selectedParentMe.numSelected === this.selectedParentMe.totalChildrenCount) {
        this.selectedParentMe.selected = true;
        this.selected.add(this.selectedParentMe.key); // add parent ME node when selection changed
      }
      changes.changedNodes.forEach((node: DfMeDropdownNode) => {
        this.selected.add(node.key); // add changed children nodes
      });
    } else {
      this.selectedParentMe.numSelected! -= changes.changedNodes.length;
      this.selectedParentMe.selected = false;
      changes.changedNodes.forEach((changedNode: DfMeDropdownNode) => {
        this.removeFromSelected(changedNode.key); // remove all unselected children nodes
      });
      this.removeFromSelected(this.selectedParentMe.key); // remove the parent ME node
    }
    this.emitChanges();
  }

  private emitChanges(): void {
    this.selectedMes.emit(Array.from(this.selected).join(','));
  }
}

interface CustomMENodeResponse {
  JSON_TEST: string;
}
